<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRoute } from 'vue-router';
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';
import whatsappCallAPI from 'dashboard/api/whatsappCall';
import { createCallSession } from 'dashboard/composables/useWhatsappWebrtc';

// ============================================================================
// WhatsApp Call — POPUP de ligação com ÁUDIO REAL (Fase 2c, voz WebRTC).
// INBOUND: chega 'whatsappCall:incoming' (com o SDP offer da Meta) -> toca ->
//   Atender monta o SDP answer no navegador e manda pra Meta (áudio conecta).
// OUTBOUND: o botão verde da conversa emite 'whatsappCall:dial' -> a gente cria
//   o SDP offer, origina na Meta e espera o SDP answer ('whatsappCall:answer').
// A mídia flui navegador <-> Meta (ICE-lite). Precisa HTTPS + microfone.
// ============================================================================

const route = useRoute();
const accountId = computed(() => route.params.accountId);

// estado da chamada corrente (só uma por vez)
const chamada = ref(null); // { id, phone_number, contato?, conversation_id, sdp_offer? }
const fase = ref('idle'); // idle | tocando | chamando | ativa
const segundos = ref(0);
let timer = null;
let session = null;

const iniciarCronometro = () => {
  segundos.value = 0;
  timer = setInterval(() => {
    segundos.value += 1;
  }, 1000);
};

const limpar = () => {
  clearInterval(timer);
  timer = null;
  if (session) {
    session.hangup();
    session = null;
  }
  chamada.value = null;
  fase.value = 'idle';
  segundos.value = 0;
};

const abrirConversa = conversationId => {
  if (!conversationId) return;
  const url = `/app/accounts/${accountId.value}/conversations/${conversationId}`;
  if (!window.location.pathname.includes(`/conversations/${conversationId}`)) {
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
};

// ---------------- INBOUND ----------------
const aoReceber = data => {
  // eslint-disable-next-line no-console
  console.log('[WACALL] incoming recebido no widget:', data);
  if (fase.value !== 'idle') return; // já tratando uma ligação
  chamada.value = data;
  fase.value = 'tocando';
  // notificação do SO como reforço (se a aba não estiver focada)
  try {
    if (window.Notification && Notification.permission === 'granted') {
      // eslint-disable-next-line no-new
      new Notification('Ligação do WhatsApp', {
        body: data.contato || data.phone_number || 'Chamada recebida',
      });
    } else if (window.Notification && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  } catch (e) {
    /* noop */
  }
};

const atender = async () => {
  try {
    session = createCallSession();
    const answerSdp = await session.answerToOffer(chamada.value.sdp_offer);
    await whatsappCallAPI.atender(accountId.value, chamada.value.id, answerSdp);
    fase.value = 'ativa';
    iniciarCronometro();
    abrirConversa(chamada.value.conversation_id);
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        e?.message ||
        'Não foi possível atender (microfone?)'
    );
    limpar();
  }
};

const recusar = async () => {
  try {
    await whatsappCallAPI.recusar(accountId.value, chamada.value.id);
  } finally {
    limpar();
  }
};

// ---------------- OUTBOUND ----------------
const ligarPara = async ({ inboxId, to, contato, conversationId }) => {
  if (fase.value !== 'idle') {
    useAlert('Já existe uma ligação em andamento.');
    return;
  }
  chamada.value = {
    id: null,
    phone_number: to,
    contato,
    conversation_id: conversationId,
  };
  fase.value = 'chamando';
  try {
    session = createCallSession();
    const offerSdp = await session.createOffer();
    const { data } = await whatsappCallAPI.originar(accountId.value, {
      inboxId,
      to,
      sdp: offerSdp,
    });
    chamada.value = { ...chamada.value, id: data.id, wa_call_id: data.wa_call_id };
    // agora espera 'whatsappCall:answer' pra abrir o áudio (aoAtenderRemoto)
  } catch (e) {
    useAlert(
      e?.response?.data?.error || e?.message || 'Não foi possível ligar'
    );
    limpar();
  }
};

// a Meta devolveu o SDP answer da nossa ligação outbound
const aoAtenderRemoto = async data => {
  if (!chamada.value || fase.value !== 'chamando') return;
  if (chamada.value.id && data.id !== chamada.value.id) return;
  try {
    await session.applyRemoteAnswer(data.sdp);
    fase.value = 'ativa';
    iniciarCronometro();
  } catch (e) {
    useAlert('Falha ao conectar o áudio.');
    limpar();
  }
};

// ---------------- comum ----------------
const encerrar = async () => {
  const id = chamada.value?.id;
  try {
    if (id) await whatsappCallAPI.encerrar(accountId.value, id);
  } finally {
    limpar();
  }
};

const aoAtualizar = data => {
  // chamada terminou/caiu em outro lugar -> fecha
  if (
    chamada.value &&
    data?.id === chamada.value.id &&
    ['ended', 'missed', 'rejected', 'failed'].includes(data.status)
  ) {
    limpar();
  }
};

const fmt = s => {
  const m = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
};

onMounted(() => {
  emitter.on('whatsappCall:incoming', aoReceber);
  emitter.on('whatsappCall:update', aoAtualizar);
  emitter.on('whatsappCall:answer', aoAtenderRemoto);
  emitter.on('whatsappCall:dial', ligarPara);
});
onBeforeUnmount(() => {
  emitter.off('whatsappCall:incoming', aoReceber);
  emitter.off('whatsappCall:update', aoAtualizar);
  emitter.off('whatsappCall:answer', aoAtenderRemoto);
  emitter.off('whatsappCall:dial', ligarPara);
  clearInterval(timer);
  if (session) session.hangup();
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <!-- ============ TOCANDO (inbound) ============ -->
  <div v-if="fase === 'tocando'" class="wcall-popup">
    <div class="wcall-anel">
      <span class="wcall-fone pisca"><span class="i-lucide-phone-incoming size-7" /></span>
    </div>
    <p class="wcall-titulo">Ligação do WhatsApp</p>
    <p class="wcall-numero tabular-nums">{{ chamada.contato || chamada.phone_number || 'Número desconhecido' }}</p>
    <p v-if="chamada.contato && chamada.phone_number" class="wcall-sub tabular-nums">{{ chamada.phone_number }}</p>
    <div class="wcall-acoes">
      <button type="button" class="wcall-btn recusar" @click="recusar">
        <span class="i-lucide-phone-off size-5" />
        Recusar
      </button>
      <button type="button" class="wcall-btn atender" @click="atender">
        <span class="i-lucide-phone size-5" />
        Atender
      </button>
    </div>
  </div>

  <!-- ============ CHAMANDO (outbound, aguardando atender) ============ -->
  <div v-else-if="fase === 'chamando'" class="wcall-popup">
    <div class="wcall-anel">
      <span class="wcall-fone pisca"><span class="i-lucide-phone-outgoing size-7" /></span>
    </div>
    <p class="wcall-titulo">Chamando…</p>
    <p class="wcall-numero tabular-nums">{{ chamada.contato || chamada.phone_number }}</p>
    <div class="wcall-acoes">
      <button type="button" class="wcall-btn recusar" @click="encerrar">
        <span class="i-lucide-phone-off size-5" />
        Cancelar
      </button>
    </div>
  </div>

  <!-- ============ EM ATENDIMENTO (áudio ao vivo) ============ -->
  <div v-else-if="fase === 'ativa'" class="wcall-popup ativa">
    <div class="flex gap-3 items-center">
      <span class="wcall-fone ativo"><span class="i-lucide-phone-call size-5" /></span>
      <div class="flex-1 min-w-0">
        <p class="wcall-titulo-min truncate">{{ chamada.contato || chamada.phone_number }}</p>
        <p class="wcall-timer tabular-nums">{{ fmt(segundos) }}</p>
      </div>
      <button type="button" class="wcall-btn recusar compacto" @click="encerrar">
        <span class="i-lucide-phone-off size-4" />
      </button>
    </div>
  </div>
</template>

<style scoped>
/* popup de ligação (tema escuro, glow verde WhatsApp) */
.wcall-popup {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 99990;
  width: 320px;
  padding: 22px 20px 20px;
  border-radius: 18px;
  background: linear-gradient(180deg, #17181d, #101115);
  border: 2px solid #25d366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.2), 0 18px 48px rgba(0, 0, 0, 0.55);
  color: #fff;
  text-align: center;
  animation: wcall-entra 0.25s ease;
}
.wcall-popup.ativa {
  width: 300px;
  padding: 14px 16px;
  text-align: left;
}
@keyframes wcall-entra {
  from { transform: translateY(16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
.wcall-anel {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}
.wcall-fone {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 62px;
  height: 62px;
  border-radius: 50%;
  background: rgba(37, 211, 102, 0.16);
  color: #25d366;
}
.wcall-fone.pisca {
  animation: wcall-pisca 1s ease-in-out infinite;
}
@keyframes wcall-pisca {
  0%, 100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.55); transform: scale(1); }
  50% { box-shadow: 0 0 0 16px rgba(37, 211, 102, 0); transform: scale(1.08); }
}
.wcall-fone.ativo {
  width: 40px;
  height: 40px;
}
.wcall-titulo {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9ca3af;
}
.wcall-numero {
  margin-top: 2px;
  font-size: 18px;
  font-weight: 800;
}
.wcall-sub {
  font-size: 12px;
  color: #9ca3af;
}
.wcall-acoes {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}
.wcall-btn {
  flex: 1;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 11px 0;
  border: none;
  border-radius: 12px;
  font-weight: 800;
  font-size: 14px;
  color: #fff;
  cursor: pointer;
  transition: transform 0.1s ease;
}
.wcall-btn:hover { transform: translateY(-1px); }
.wcall-btn.recusar { background: #e5484d; }
.wcall-btn.atender { background: #25d366; color: #06301a; }
.wcall-btn.compacto { flex: 0 0 40px; padding: 10px 0; border-radius: 10px; }
.wcall-titulo-min { font-size: 14px; font-weight: 700; }
.wcall-timer { font-size: 12px; color: #25d366; font-weight: 700; }
</style>
